import type { GeneratedImage } from '../services/firebase';

export type CachedGalleryImage = {
  id: string;
  generatedImage: string;
  originalImage: string;
  prompt: string;
  createdAt: string;
  background?: string;
  outfit?: string;
  pose?: string;
  settings?: unknown;
  source: 'local' | 'firestore';
  showcaseId?: string;
};

export type GalleryCachePayload = {
  images: CachedGalleryImage[];
  showcaseMap: Record<string, string>;
  syncedAt: string;
};

const GALLERY_CACHE_PREFIX = 'gallery_cache_v2_';
/** この時間以内なら DB 同期をスキップ（即表示のみ） */
const FRESH_TTL_MS = 2 * 60 * 1000;
/** 期限切れでも表示用には使う（裏で更新） */
const STALE_TTL_MS = 24 * 60 * 60 * 1000;

const readCache = (
  userId: string,
  options: { allowStale?: boolean } = {},
): GalleryCachePayload | null => {
  try {
    const raw = localStorage.getItem(`${GALLERY_CACHE_PREFIX}${userId}`);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as GalleryCachePayload;
    if (!parsed?.images || !parsed.syncedAt) return null;

    const age = Date.now() - new Date(parsed.syncedAt).getTime();
    if (age > STALE_TTL_MS) return null;
    // allowStale=false は「新鮮」のみ。true なら期限切れでも表示用に返す
    if (!options.allowStale && age > FRESH_TTL_MS) return null;

    return parsed;
  } catch {
    return null;
  }
};

const writeCache = (userId: string, payload: GalleryCachePayload) => {
  try {
    localStorage.setItem(`${GALLERY_CACHE_PREFIX}${userId}`, JSON.stringify(payload));
  } catch (error) {
    console.warn('Gallery cache write failed:', error);
  }
};

export const invalidateGalleryCache = (userId: string) => {
  try {
    localStorage.removeItem(`${GALLERY_CACHE_PREFIX}${userId}`);
  } catch {
    // ignore
  }
};

const isValidGalleryImage = (img: CachedGalleryImage) =>
  Boolean(img?.id && img?.generatedImage && img?.createdAt);

const loadLocalGalleryImages = (userId: string): CachedGalleryImage[] => {
  try {
    const raw = localStorage.getItem(`gallery_${userId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((img: any) => ({
        id: img.id,
        generatedImage: img.generatedImage || img.imageUrl || '',
        originalImage: img.originalImage || '',
        prompt: img.prompt || '',
        createdAt:
          typeof img.createdAt === 'string'
            ? img.createdAt
            : img.createdAt?.toISOString?.() || new Date().toISOString(),
        background: img.background || img.template,
        outfit: img.outfit,
        pose: img.pose,
        settings: img.settings ?? null,
        source: 'local' as const,
        showcaseId: img.showcaseId,
      }))
      .filter(isValidGalleryImage);
  } catch {
    return [];
  }
};

const hydrateGalleryImages = (
  images: CachedGalleryImage[],
  showcaseMap: Record<string, string>,
  showcaseByUrl: Record<string, string> = {},
) =>
  images
    .map((img) => ({
      ...img,
      createdAt: img.createdAt,
      showcaseId:
        showcaseMap[img.id] ||
        showcaseByUrl[img.generatedImage] ||
        img.showcaseId ||
        undefined,
    }))
    .filter(isValidGalleryImage)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

export const toGalleryViewModels = (images: CachedGalleryImage[]) =>
  images.map((img) => ({
    ...img,
    createdAt: new Date(img.createdAt),
  }));

type LoadUserGalleryOptions = {
  onCached?: (images: ReturnType<typeof toGalleryViewModels>) => void;
  /** true のとき 2分以内のキャッシュなら DB を叩かない */
  skipFreshDb?: boolean;
};

/**
 * ギャラリー読み込み。
 * 1) localStorage（新鮮 or 期限切れ）を即表示
 * 2) 裏で Firestore 同期（空結果では既存キャッシュを潰さない）
 */
export const loadUserGallery = async (
  userId: string,
  options: LoadUserGalleryOptions = {},
) => {
  const fresh = readCache(userId, { allowStale: false });
  const stale = fresh ? null : readCache(userId, { allowStale: true });
  const localOnly = hydrateGalleryImages(loadLocalGalleryImages(userId), {});

  const instant =
    fresh?.images ||
    stale?.images ||
    (localOnly.length > 0 ? localOnly : null);

  if (instant && instant.length > 0) {
    options.onCached?.(toGalleryViewModels(instant));
  }

  // 新鮮なキャッシュがあれば DB スキップ（最近の生成の高速表示用）
  if (options.skipFreshDb && fresh && fresh.images.length > 0) {
    return toGalleryViewModels(fresh.images);
  }

  const { getUserGeneratedImages, getUserShowcaseItems } = await import('../services/firebase');

  let dbImages: GeneratedImage[] = [];
  let userShowcaseItems: Awaited<ReturnType<typeof getUserShowcaseItems>> = [];

  try {
    const [imagesResult, showcaseResult] = await Promise.all([
      getUserGeneratedImages(userId),
      getUserShowcaseItems(userId).catch((err) => {
        console.warn('Showcase linkage fetch failed:', err);
        return [] as Awaited<ReturnType<typeof getUserShowcaseItems>>;
      }),
    ]);
    dbImages = imagesResult;
    userShowcaseItems = showcaseResult;
  } catch (error) {
    console.error('Gallery DB sync failed:', error);
    // DB 失敗時は即時表示分を返す（空で上書きしない）
    if (instant && instant.length > 0) {
      return toGalleryViewModels(instant);
    }
    throw error;
  }

  // 一時的な空レスポンスで既存データを消さない（認証直後のレース等）
  if (dbImages.length === 0 && instant && instant.length > 0) {
    console.warn('Gallery DB returned empty; keeping cached/local images');
    return toGalleryViewModels(instant);
  }

  const showcaseMap: Record<string, string> = {};
  const showcaseByUrl: Record<string, string> = {};
  userShowcaseItems.forEach((item) => {
    if (item.imageId) {
      showcaseMap[item.imageId] = item.id;
    }
    if (item.imageUrl) {
      showcaseByUrl[item.imageUrl] = item.id;
    }
  });

  const firestoreImages: CachedGalleryImage[] = dbImages.map((img: GeneratedImage) => ({
    id: img.id,
    generatedImage: img.imageUrl || img.imageData || '',
    originalImage: '',
    prompt: img.prompt,
    createdAt: img.createdAt,
    background: img.template,
    outfit: img.outfit,
    pose: img.pose,
    settings: (img as GeneratedImage & { settings?: unknown }).settings ?? null,
    source: 'firestore' as const,
  }));

  const merged = hydrateGalleryImages(
    Array.from(
      new Map(
        [...loadLocalGalleryImages(userId), ...firestoreImages].map((img) => [img.id, img]),
      ).values(),
    ),
    showcaseMap,
    showcaseByUrl,
  );

  writeCache(userId, {
    images: merged,
    showcaseMap,
    syncedAt: new Date().toISOString(),
  });

  return toGalleryViewModels(merged);
};
